import React, { useState, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { MemberFormFields } from "./MemberFormFields";
import { api } from "../lib/api";
import { useDebouncedEffect } from "../hooks/useDebouncedEffect";
import { toast } from "sonner";
import { ImagePlus, Sparkles, Users, UserPlus, Check, Loader2, AlertTriangle } from "lucide-react";

const relationLabel = { parent: "Add a parent", child: "Add a child", spouse: "Add a spouse / partner", sibling: "Add a sibling" };
const relLabelShort = { parent: "parent", child: "child", spouse: "spouse / partner", sibling: "sibling" };
const invert = { parent: "child", child: "parent", spouse: "spouse", sibling: "sibling" };

const FIELDS = ["full_name", "nickname", "first_name", "middle_name", "last_name", "former_name", "gender", "birth_date", "death_date", "origin_place", "current_place", "bio"];
const pick = (form) => Object.fromEntries(FIELDS.map((k) => [k, form[k] ?? ""]));

export function AddMemberDialog({ open, onOpenChange, relateTo, relation, relateToName, spouseNames = [], onDone }) {
  const [form, setForm] = useState({});
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | saving | saved
  const [paragraph, setParagraph] = useState("");
  const [reading, setReading] = useState(false);
  const [autofilled, setAutofilled] = useState(false);
  const [relatives, setRelatives] = useState([]);
  const [fatherName, setFatherName] = useState("");
  const [motherName, setMotherName] = useState("");
  const [includeSpouse, setIncludeSpouse] = useState(true);
  const [fatherMatches, setFatherMatches] = useState([]);
  const [motherMatches, setMotherMatches] = useState([]);
  const [fatherChoice, setFatherChoice] = useState("new");
  const [motherChoice, setMotherChoice] = useState("new");

  const createdId = useRef(null);
  const photoUploaded = useRef(false);
  const lock = useRef(false);

  useEffect(() => {
    if (open) {
      setForm({});
      setFile(null);
      setPreview(null);
      setParagraph("");
      setAutofilled(false);
      setRelatives([]);
      setFatherName("");
      setMotherName("");
      setIncludeSpouse(true);
      setFatherMatches([]);
      setMotherMatches([]);
      setFatherChoice("new");
      setMotherChoice("new");
      setStatus("idle");
      createdId.current = null;
      photoUploaded.current = false;
    }
  }, [open]);

  const persist = async () => {
    if (lock.current) return;
    if (!form.full_name?.trim()) return; // never create a blank person
    lock.current = true;
    setStatus("saving");
    try {
      const payload = pick(form);
      if (!createdId.current) {
        if (relateTo && relation) {
          payload.relate_to = relateTo;
          payload.relation = relation;
          if (relation === "child") payload.link_couple = includeSpouse;
        }
        const created = await api.create(payload);
        createdId.current = created.id;
        if (file && !photoUploaded.current) {
          photoUploaded.current = true;
          await api.uploadPhoto(created.id, file);
        }
        onDone?.();
      } else {
        await api.update(createdId.current, payload);
      }
      setStatus("saved");
    } catch {
      setStatus("idle");
    } finally {
      lock.current = false;
    }
  };

  // Auto-save while typing
  useDebouncedEffect(() => { persist(); }, [JSON.stringify(pick(form))], 900);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    if (createdId.current) {
      photoUploaded.current = true;
      api.uploadPhoto(createdId.current, f).then(() => onDone?.());
    }
  };

  const readParagraph = async () => {
    if (!paragraph.trim()) {
      toast.error("Paste a paragraph first");
      return;
    }
    setReading(true);
    try {
      const data = await api.autofillPerson(paragraph);
      setForm((f) => ({
        ...f,
        full_name: data.full_name || f.full_name || "",
        gender: data.gender || f.gender,
        birth_date: data.birth_date || f.birth_date || "",
        death_date: data.death_date || f.death_date || "",
        origin_place: data.origin_place || f.origin_place || "",
        current_place: data.current_place || f.current_place || "",
        bio: data.bio || f.bio || "",
      }));
      setRelatives(
        (data.relatives || []).map((r) => ({
          ...r,
          accepted: true,
          choice: r.match ? r.match.id : r.candidates?.length === 1 ? r.candidates[0].id : "new",
        }))
      );
      setAutofilled(true);
      toast.success("Filled in what I could — review below");
    } catch {
      toast.error("Could not read the paragraph");
    } finally {
      setReading(false);
    }
  };

  const toggleRel = (i) => setRelatives((rs) => rs.map((r, idx) => (idx === i ? { ...r, accepted: !r.accepted } : r)));
  const setChoice = (i, v) => setRelatives((rs) => rs.map((r, idx) => (idx === i ? { ...r, choice: v } : r)));

  const checkParent = async (name, setMatches, setChoiceFn) => {
    const n = name.trim();
    if (!n) { setMatches([]); setChoiceFn("new"); return; }
    try {
      const { candidates } = await api.nameMatches(n);
      setMatches(candidates || []);
      const exact = (candidates || []).find((c) => c.exact);
      setChoiceFn(exact ? exact.id : "new");
    } catch { setMatches([]); }
  };

  const linkOrCreateParent = async (id, name, gender, choice) => {
    const n = name.trim();
    if (!n) return;
    if (choice && choice !== "new") {
      await api.link(id, { target_id: choice, relation: "child" });
    } else {
      await api.create({ full_name: n, gender, relate_to: id, relation: "parent" });
    }
  };

  const renderParentMatch = (label, name, matches, choice, setChoiceFn) => {
    if (!name.trim() || matches.length === 0) return null;
    return (
      <div className="col-span-2 rounded-xl border border-[#D4A017]/40 bg-white p-2" data-testid={`${label}-match-warning`}>
        <div className="mb-1.5 flex items-center gap-1.5 text-xs font-medium text-[#D4A017]">
          <AlertTriangle size={13} /> "{name.trim()}" may already be in your tree
        </div>
        <Select value={choice} onValueChange={setChoiceFn}>
          <SelectTrigger data-testid={`${label}-match-select`} className="h-8 bg-white text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {matches.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                Link to {c.full_name}{c.birth_date ? ` (${c.birth_date})` : ""}{c.current_place ? ` — ${c.current_place}` : ""}
              </SelectItem>
            ))}
            <SelectItem value="new">Create "{name.trim()}" as a new person</SelectItem>
          </SelectContent>
        </Select>
      </div>
    );
  };

  const submit = async () => {
    if (!form.full_name?.trim()) {
      toast.error("Please enter a name");
      return;
    }
    setSaving(true);
    try {
      // ensure the person exists & latest fields saved
      await persist();
      let id = createdId.current;
      if (!id) {
        const payload = pick(form);
        if (relateTo && relation) {
          payload.relate_to = relateTo; payload.relation = relation;
          if (relation === "child") payload.link_couple = includeSpouse;
        }
        const created = await api.create(payload);
        id = created.id;
        createdId.current = id;
      }
      if (file && !photoUploaded.current) {
        photoUploaded.current = true;
        await api.uploadPhoto(id, file);
      }
      if (fatherName.trim()) await linkOrCreateParent(id, fatherName, "male", fatherChoice);
      if (motherName.trim()) await linkOrCreateParent(id, motherName, "female", motherChoice);
      for (const rel of relatives.filter((r) => r.accepted)) {
        try {
          if (rel.choice && rel.choice !== "new") {
            await api.link(id, { target_id: rel.choice, relation: invert[rel.relation] });
          } else {
            await api.create({ full_name: rel.name, relate_to: id, relation: rel.relation });
          }
        } catch (e) { console.error("Failed to link/create relative, continuing", e); }
      }
      toast.success(`${form.full_name} added to the family`);
      onDone?.();
      onOpenChange(false);
    } catch {
      toast.error("Could not save member");
    } finally {
      setSaving(false);
    }
  };

  const title = relation ? relationLabel[relation] : "Add a family member";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="add-member-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="font-serif text-2xl text-[#082D4A]">{title}</DialogTitle>
            {status !== "idle" && (
              <span data-testid="autosave-indicator" className="flex items-center gap-1 text-xs text-[#64748B]">
                {status === "saving" ? <><Loader2 size={13} className="animate-spin" /> Saving…</> : <><Check size={13} className="text-[#082D4A]" /> Saved</>}
              </span>
            )}
          </div>
          <DialogDescription>
            {relateToName ? (
              <>Related to <span className="font-medium text-[#D4A017]">{relateToName}</span></>
            ) : (
              "Fill in what you know — or paste a paragraph and let AI do it. Changes save as you type."
            )}
          </DialogDescription>
        </DialogHeader>

        {relation === "child" && spouseNames.length > 0 && (
          <label className="flex items-center gap-2.5 rounded-xl border border-[#D8E0E8] bg-[#F4F6F8] p-3 text-sm" data-testid="include-spouse-toggle">
            <Checkbox checked={includeSpouse} onCheckedChange={(v) => setIncludeSpouse(!!v)} data-testid="include-spouse-checkbox" />
            <span className="text-[#475569]">Also make <span className="font-medium text-[#082D4A]">{spouseNames.join(" & ")}</span> a parent</span>
          </label>
        )}

        <div className="rounded-2xl border border-[#D4A017]/30 bg-[#D4A017]/5 p-3">
          <Label className="flex items-center gap-1.5 text-sm font-semibold text-[#D4A017]">
            <Sparkles size={15} /> Quick fill from a paragraph
          </Label>
          <Textarea
            data-testid="autofill-paragraph-input"
            value={paragraph}
            onChange={(e) => setParagraph(e.target.value)}
            rows={3}
            className="mt-2 bg-white"
            placeholder="e.g. My name is Mary Johnson, born 1948 in Cork, Ireland. I live in Boston. My husband is Tom and my daughter is Anne."
          />
          <Button onClick={readParagraph} disabled={reading} data-testid="autofill-read-button" className="mt-2 w-full bg-[#D4A017] text-white hover:bg-[#B0850F]">
            {reading ? "Reading…" : "Read paragraph & fill fields"}
          </Button>
        </div>

        <div className="space-y-5 py-2">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 overflow-hidden rounded-full border border-[#D8E0E8] bg-[#E8EEF3]">
              {preview && <img src={preview} alt="preview" className="h-full w-full object-cover" />}
            </div>
            <div>
              <Label htmlFor="photo" className="cursor-pointer">
                <span data-testid="member-photo-upload-label" className="inline-flex items-center gap-2 rounded-full border border-[#D8E0E8] bg-white px-3 py-1.5 text-sm font-medium text-[#082D4A] hover:bg-[#E8EEF3]">
                  <ImagePlus size={16} /> Add photo
                </span>
              </Label>
              <Input id="photo" data-testid="member-photo-upload" type="file" accept="image/*" className="hidden" onChange={onFile} />
            </div>
          </div>

          <MemberFormFields form={form} setForm={setForm} />

          <div className="grid grid-cols-2 gap-3 rounded-2xl border border-[#D8E0E8] bg-[#F4F6F8] p-3">
            <div className="col-span-2 text-xs font-semibold uppercase tracking-wider text-[#64748B]">Parents (optional — creates & links them)</div>
            <div className="space-y-1">
              <Label htmlFor="father_name" className="text-xs">Father's name</Label>
              <Input id="father_name" data-testid="member-father-input" className="bg-white" value={fatherName} onChange={(e) => setFatherName(e.target.value)} onBlur={() => checkParent(fatherName, setFatherMatches, setFatherChoice)} placeholder="e.g. Joseph Walters" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="mother_name" className="text-xs">Mother's name</Label>
              <Input id="mother_name" data-testid="member-mother-input" className="bg-white" value={motherName} onChange={(e) => setMotherName(e.target.value)} onBlur={() => checkParent(motherName, setMotherMatches, setMotherChoice)} placeholder="e.g. Grace Walters" />
            </div>
            {renderParentMatch("father", fatherName, fatherMatches, fatherChoice, setFatherChoice)}
            {renderParentMatch("mother", motherName, motherMatches, motherChoice, setMotherChoice)}
          </div>

          {autofilled && relatives.length > 0 && (
            <div className="rounded-2xl border border-[#D8E0E8] bg-white p-3" data-testid="autofill-relatives-review">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#64748B]">Relatives found — untick any to skip</div>
              <div className="space-y-2">
                {relatives.map((rel, i) => (
                  <div key={i} data-testid={`autofill-relative-${i}`} className="rounded-xl bg-[#F4F6F8] p-2 text-sm">
                    <label className="flex cursor-pointer items-center gap-2.5">
                      <Checkbox checked={rel.accepted} onCheckedChange={() => toggleRel(i)} data-testid={`autofill-relative-${i}-checkbox`} />
                      {rel.choice !== "new" ? <Users size={15} className="text-[#082D4A]" /> : <UserPlus size={15} className="text-[#D4A017]" />}
                      <span className="flex-1 text-[#082D4A]">
                        <span className="font-medium">{rel.name}</span> <span className="text-[#64748B]">as {relLabelShort[rel.relation]}</span>
                      </span>
                    </label>
                    {rel.accepted && rel.candidates && rel.candidates.length > 1 && (
                      <div className="mt-2 pl-7">
                        <div className="mb-1 text-xs text-[#D4A017]">More than one match — pick one:</div>
                        <Select value={rel.choice} onValueChange={(v) => setChoice(i, v)}>
                          <SelectTrigger data-testid={`autofill-relative-${i}-chooser`} className="h-8 bg-white text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {rel.candidates.map((c) => (
                              <SelectItem key={c.id} value={c.id}>
                                {c.full_name}{c.birth_date ? ` (${c.birth_date})` : ""}{c.current_place ? ` — ${c.current_place}` : ""}
                              </SelectItem>
                            ))}
                            <SelectItem value="new">Add {rel.name} as a new person</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cancel-member-button">Close</Button>
          <Button onClick={submit} disabled={saving} data-testid="save-member-button" className="bg-[#082D4A] text-white hover:bg-[#08223A]">
            {saving ? "Saving…" : "Done"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
